import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import DraggableModal from "../layout/DraggableModal/DraggableModal";
import {
  deleteCharacterAvatar,
  updateCharacterAvatarUrl,
  uploadCharacterAvatar,
} from "../../clients/characterAvatar.client";
import { useCharacterAvatarSource } from "../../hooks/useCharacterAvatarSource";

const MAX_IMAGE_FILE_SIZE = 5 * 1024 * 1024;
const OUTPUT_WIDTH = 640;
const OUTPUT_HEIGHT = 800;
const SUPPORTED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImage = (source) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

export function getCharacterCropRect(imageWidth, imageHeight, crop) {
  const coverScale = Math.max(OUTPUT_WIDTH / imageWidth, OUTPUT_HEIGHT / imageHeight);
  const cropWidth = OUTPUT_WIDTH / (coverScale * crop.zoom);
  const cropHeight = OUTPUT_HEIGHT / (coverScale * crop.zoom);

  return {
    height: cropHeight,
    width: cropWidth,
    x: (imageWidth - cropWidth) * (crop.x / 100),
    y: (imageHeight - cropHeight) * (crop.y / 100),
  };
}

const drawCharacterCrop = (canvas, image, crop) => {
  const source = getCharacterCropRect(image.width, image.height, crop);
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;
  canvas.getContext("2d").drawImage(
    image,
    source.x,
    source.y,
    source.width,
    source.height,
    0,
    0,
    OUTPUT_WIDTH,
    OUTPUT_HEIGHT
  );
};

const CharacterAvatar = ({ character, onChange, token }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 50, y: 50, zoom: 1 });
  const fileInputRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const cropZoomRef = useRef(null);
  const avatarSource = useCharacterAvatarSource(character.avatarUrl, token);

  useEffect(() => setImageLoadFailed(false), [character.avatarUrl]);

  useEffect(() => {
    if (cropImage && cropCanvasRef.current) {
      drawCharacterCrop(cropCanvasRef.current, cropImage, crop);
    }
  }, [crop, cropImage]);

  useEffect(() => {
    if (cropImage) cropZoomRef.current?.focus();
  }, [cropImage]);

  const hasImage = Boolean(avatarSource) && !imageLoadFailed;

  const openDialog = () => {
    setImageUrl(character.avatarUrl?.startsWith("http") ? character.avatarUrl : "");
    setError("");
    setCropImage(null);
    setIsOpen(true);
  };

  const closeDialog = () => {
    if (!isProcessing) setIsOpen(false);
  };

  const finishImageUpdate = (avatarUrl) => {
    onChange({ ...character, avatarUrl });
    setImageLoadFailed(false);
    setIsOpen(false);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!SUPPORTED_IMAGE_TYPES.has(file.type)) {
      setError(t("character.imageTypeError"));
      return;
    }
    if (file.size > MAX_IMAGE_FILE_SIZE) {
      setError(t("character.imageSizeError"));
      return;
    }

    setError("");
    setIsProcessing(true);
    try {
      const source = await readFileAsDataUrl(file);
      const image = await loadImage(source);
      setCrop({ x: 50, y: 50, zoom: 1 });
      setCropImage(image);
    } catch {
      setError(t("character.imageReadError"));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCropChange = (property, value) => {
    const numberValue = Number(value);
    setCrop((current) => ({
      ...current,
      [property]: property === "zoom"
        ? clamp(numberValue, 1, 3)
        : clamp(numberValue, 0, 100),
    }));
  };

  const saveCroppedImage = async () => {
    if (!cropCanvasRef.current) return;
    setIsProcessing(true);
    setError("");
    const response = await uploadCharacterAvatar(
      character.id,
      cropCanvasRef.current.toDataURL("image/webp", 0.82),
      token
    );
    setIsProcessing(false);
    if (response.isError || !response.data?.avatarUrl) {
      setError(t("character.imageSaveError"));
      return;
    }
    finishImageUpdate(response.data.avatarUrl);
  };

  const handleUrlSubmit = async (event) => {
    event.preventDefault();
    const normalizedUrl = imageUrl.trim();

    try {
      const parsedUrl = new URL(normalizedUrl);
      if (parsedUrl.protocol !== "https:") throw new Error("Unsupported protocol");
    } catch {
      setError(t("character.imageUrlError"));
      return;
    }

    setIsProcessing(true);
    const response = await updateCharacterAvatarUrl(character.id, normalizedUrl, token);
    setIsProcessing(false);
    if (response.isError || !response.data?.avatarUrl) {
      setError(t("character.imageSaveError"));
      return;
    }
    finishImageUpdate(response.data.avatarUrl);
  };

  const removeImage = async () => {
    setIsProcessing(true);
    setError("");
    const response = await deleteCharacterAvatar(character.id, token);
    setIsProcessing(false);
    if (response.isError) {
      setError(t("character.imageSaveError"));
      return;
    }
    finishImageUpdate("");
  };

  return (
    <>
      <button
        aria-label={t("character.changeImage")}
        className="character-avatar"
        type="button"
        onClick={openDialog}
      >
        {hasImage ? (
          <img alt="" src={avatarSource} onError={() => setImageLoadFailed(true)} />
        ) : (
          <span className="character-avatar__placeholder" aria-hidden="true">+</span>
        )}
      </button>

      <DraggableModal
        className="character-image-dialog"
        closeDisabled={isProcessing}
        headerText={t("character.imageDialogTitle")}
        initialFocusRef={fileInputRef}
        isVisible={isOpen}
        onClose={closeDialog}
      >
        <div className="character-image-dialog__content">
          {cropImage ? (
            <div className="character-crop-editor">
              <p className="character-crop-hint">{t("character.cropHint")}</p>
              <div className="character-crop-preview">
                <canvas aria-label={t("character.cropPreview")} ref={cropCanvasRef} role="img" />
              </div>
              <div className="character-crop-controls">
                <label htmlFor="character-crop-zoom">{t("character.cropZoom")}</label>
                <input id="character-crop-zoom" max="3" min="1" ref={cropZoomRef} step="0.05" type="range" value={crop.zoom} onChange={(event) => handleCropChange("zoom", event.target.value)} />
                <label htmlFor="character-crop-x">{t("character.cropHorizontal")}</label>
                <input id="character-crop-x" max="100" min="0" type="range" value={crop.x} onChange={(event) => handleCropChange("x", event.target.value)} />
                <label htmlFor="character-crop-y">{t("character.cropVertical")}</label>
                <input id="character-crop-y" max="100" min="0" type="range" value={crop.y} onChange={(event) => handleCropChange("y", event.target.value)} />
              </div>
              {error && <p className="character-image-error" role="alert">{error}</p>}
              {isProcessing && <p className="character-image-status" role="status">{t("character.processingImage")}</p>}
              <div className="character-image-actions">
                <button disabled={isProcessing} type="button" onClick={() => {
                  setCropImage(null);
                }}>{t("character.chooseAnotherImage")}</button>
                <button className="character-image-save" disabled={isProcessing} type="button" onClick={saveCroppedImage}>{t("character.applyCrop")}</button>
              </div>
            </div>
          ) : (
            <>
              <label className="character-image-file" htmlFor="character-image-file">
                <span>{t("character.chooseFromDevice")}</span>
                <input
                  accept="image/jpeg,image/png,image/webp"
                  disabled={isProcessing}
                  id="character-image-file"
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                />
              </label>
              <small>{t("character.imageFileHint")}</small>

              <div className="character-image-divider"><span>{t("character.orUseLink")}</span></div>

              <form onSubmit={handleUrlSubmit}>
                <label htmlFor="character-image-url">{t("character.imageUrl")}</label>
                <input
                  aria-describedby={error ? "character-image-hint character-image-error" : "character-image-hint"}
                  aria-invalid={Boolean(error)}
                  disabled={isProcessing}
                  id="character-image-url"
                  name="characterImageUrl"
                  placeholder="https://example.com/hero.jpg"
                  type="url"
                  value={imageUrl}
                  onChange={(event) => {
                    setImageUrl(event.target.value);
                    setError("");
                  }}
                />
                <small id="character-image-hint">{t("character.imageUrlHint")}</small>
                {error && <p className="character-image-error" id="character-image-error" role="alert">{error}</p>}
                {isProcessing && <p className="character-image-status" role="status">{t("character.processingImage")}</p>}
                <div className="character-image-actions">
                  {character.avatarUrl && (
                    <button className="character-image-remove" disabled={isProcessing} type="button" onClick={removeImage}>
                      {t("character.removeImage")}
                    </button>
                  )}
                  <button disabled={isProcessing} type="button" onClick={closeDialog}>{t("common.cancel")}</button>
                  <button className="character-image-save" disabled={isProcessing} type="submit">{t("character.saveImage")}</button>
                </div>
              </form>
            </>
          )}
        </div>
      </DraggableModal>
    </>
  );
};

export default CharacterAvatar;
